import { NavigateFunction } from 'react-router-dom';

import { create } from 'zustand';

import { BookingApi, InmateApi } from '@/api';
import { IAddress } from '@/models';
import { IBooking } from '@/pages/JMS/Managament/types/booking';
import { JmsManagementRoutes } from '@/pages/JMS/Managament/utils/utils';
import { ConfirmTypes, ToastProps } from '@/store';

import { clearEmptyPhoneNumbers } from '../Managament/utils/helpers';

export enum TabStatus {
  SUBMITTED = 'SUBMITTED',
  PENDING = 'PENDING',
}

export enum TabName {
  IDENTITY = 'identity',
  DETAILS = 'details',
  FINGERPRINTS = 'fingerprints',
  PROPERTIES = 'properties',
  MEDICAL = 'medical',
  LEGAL = 'legal',
  RISK_ASSESSMENT = 'risk',
  HOUSING = 'housing',
  NOTES = 'notes',
  BOOKING_HISTORY = 'booking-history',
}

interface BookingStore {
  setBooking: (booking: IBooking | undefined) => void;
  setIsBookingLoading: (isBookingLoading: boolean) => void;
  clearBooking: () => void;
  deleteBooking: ({
    reason,
    isModalOpen,
    navigate,
    updateToast,
  }: {
    reason?: string;
    isModalOpen?: boolean;
    navigate?: NavigateFunction;
    updateToast?: (data: Partial<ToastProps>) => void;
  }) => void;

  submitTabForm: (params: {
    tabName: TabName;
    data: any;
    navigate?: NavigateFunction;
    updateToast?: (data: Partial<ToastProps>) => void;
    metadata?: any;
  }) => Promise<void>;

  booking?: IBooking;
  deleteBookingData: {
    deleteReason?: string;
    isModalOpen?: boolean;
  };
  isBookingLoading?: boolean;
  setIsLinkedFromPerson: (isLinkedFromPerson: boolean) => void;
  isLinkedFromPerson?: boolean;
}

export const useBookingStore = create<BookingStore>((set, get) => ({
  booking: undefined,
  deleteBookingData: {
    deleteReason: '',
    isModalOpen: false,
  },
  isBookingLoading: false,

  setBooking: (booking: IBooking | undefined) => {
    if (booking) {
      set({ booking });
    }
  },

  clearBooking: () => {
    set({
      booking: undefined,
      isLinkedFromPerson: false,
    });
  },

  deleteBooking: async ({ reason, isModalOpen, navigate, updateToast }) => {
    const { booking } = get();

    if (reason && booking?._id) {
      try {
        await BookingApi.deleteBooking(booking._id, reason);
        navigate?.(JmsManagementRoutes.Bookings);
        set({
          booking: undefined,
          isLinkedFromPerson: false,
          deleteBookingData: {
            deleteReason: '',
            isModalOpen: false,
          },
        });
        updateToast?.({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: 'Booking is successfully deleted.',
        });
      } catch {
        set({
          deleteBookingData: {
            deleteReason: reason,
            isModalOpen: true,
          },
        });
        updateToast?.({
          type: ConfirmTypes.ERROR,
          open: true,
          message: 'Booking deletion failed!',
        });
      }
    } else {
      set({
        deleteBookingData: {
          deleteReason: reason,
          isModalOpen,
        },
      });
    }
  },

  submitTabForm: async ({ tabName, data, navigate, updateToast, metadata }) => {
    const { booking } = get();

    try {
      let tempBooking: IBooking;

      switch (tabName) {
        case TabName.IDENTITY: {
          if (!metadata.bookingId && !metadata.inmateId) {
            tempBooking = {
              submittedData: {
                [TabName.IDENTITY]: {
                  status: TabStatus.SUBMITTED,
                },
              },
              ...data,
              identity: {
                ...data.identity,
                _id: booking?.identity?._id,
                phoneNumbers: clearEmptyPhoneNumbers(
                  data.identity.phoneNumbers,
                ),
                // clear empty object from addresses
                addresses: data.identity.addresses.filter(
                  (address: IAddress) => Object.keys(address).length > 0,
                ),
              },
            };

            if (!tempBooking.inmate?.iid) {
              delete tempBooking?.inmate;
            }

            set(() => ({
              booking: tempBooking,
              loading: false,
            }));
            return;
          }

          if (metadata.inmateId) {
            tempBooking = {
              identity: (
                await InmateApi.updateIdentity(
                  {
                    masterName: {
                      ...data.identity,
                      phoneNumbers: clearEmptyPhoneNumbers(
                        data.identity.phoneNumbers,
                      ),
                      // clear empty object from addresses
                      addresses: data.identity.addresses.filter(
                        (address: IAddress) => Object.keys(address).length > 0,
                      ),
                    },
                  },
                  booking?.inmate?._id,
                )
              ).data.masterName,
            };
            return;
          }

          tempBooking = (await BookingApi.updateIdentity(data, booking?._id))
            .data;
          break;
        }

        case TabName.DETAILS: {
          if (!booking?._id) {
            const { data: booked } = await BookingApi.create({
              ...booking,
              details: data.details,
            });
            tempBooking = booked;
            navigate?.(JmsManagementRoutes.Bookings);
            updateToast?.({
              type: ConfirmTypes.SUCCESS,
              open: true,
              message: 'Inmate successfully booked',
            });
          } else {
            const { data: updatedBooking } = await BookingApi.updateDetails(
              booking._id,
              data,
            );
            updateToast?.({
              type: ConfirmTypes.SUCCESS,
              open: true,
              message: 'Booking updated successfully',
            });
            tempBooking = updatedBooking;
          }
          break;
        }

        case TabName.FINGERPRINTS: {
          if (!booking?._id) {
            return;
          }
          const { data: updatedBooking } = await BookingApi.updateFingerprints(
            booking?._id,
            data,
          );
          tempBooking = updatedBooking;
          break;
        }
        case TabName.PROPERTIES:
          // updatedInmate = await InmateApi.updateProperties(
          //   currentInmate._id,
          //   formData,
          // );
          break;
        case TabName.MEDICAL:
          // updatedInmate = await InmateApi.updateMedical(
          //   currentInmate._id,
          //   formData,
          // );
          break;
        case TabName.HOUSING: {
          if (!booking?._id) {
            return;
          }
          const { data: updatedBooking } = await BookingApi.updateHousing(
            booking?._id,
            data,
          );
          tempBooking = updatedBooking;
          break;
        }
        default:
          throw new Error(`Unknown tab: ${tabName}`);
      }

      set(() => ({
        booking: tempBooking,
        loading: false,
      }));

      return await Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  setIsBookingLoading: (isBookingLoading: boolean) => {
    set(() => ({
      isBookingLoading,
    }));
  },

  setIsLinkedFromPerson: (isLinkedFromPerson: boolean) => {
    set(() => ({
      isLinkedFromPerson,
    }));
  },
}));
